import React, { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ISanityImage, ISanityItemOption } from '@rbi-ctg/menu';
import Incrementor from 'components/incrementor';
import MenuLineItem from 'components/menu-order-wizard/menu-line-item';
import ModifierPrice from 'components/menu-order-wizard/modifier-price';
import SelectionIndicator from 'components/selection-indicator';
import { Size } from 'components/selection-indicator/constants';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import theme from './theme';

const QuantityDetail = styled.div`
  align-items: center;
  border-top: 1px solid ${Styles.color.grey.four};
  display: flex;
  height: 2.5rem;
  padding: ${theme.quantityDetailPadding};
  margin: ${theme.quantityDetailMargin};
  width: 100%;
  flex: 1 1 100%;
`;

const ModifierSelectionPrice = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9375rem;
  font-family: ${Styles.fontFamily.body};
  color: ${theme.modifierSelectionPriceColor};
`;

interface IProps {
  checked: boolean;
  comboSlotId?: string;
  'data-testid'?: string;
  disabled?: boolean;
  image?: ISanityImage;
  imageDescription?: string;
  inputName?: string;
  itemId: string;
  itemOption: ISanityItemOption;
  name: string;
  price: number;
  quantity: number;
  subtitle?: string;
  testId?: string;
  value: string;
  onChange(event: React.ChangeEvent<HTMLInputElement>): void;
  onQuantityUpdate(
    newQuantity: number,
    itemOptionKey: string,
    itemId: string,
    key: string,
    oldQuantity: number,
    maxAmount: number,
    comboSlotId?: string
  ): void;
}

/** @deprecated Use ProductListItem with a Radio control instead */
const MenuOptionSelector = ({
  checked,
  comboSlotId,
  'data-testid': dataTestId,
  disabled,
  image,
  imageDescription,
  inputName,
  itemId,
  itemOption,
  name,
  onChange,
  onQuantityUpdate,
  price,
  quantity,
  subtitle,
  testId,
  value,
}: IProps) => {
  const [prevQuantity, setPrevQuantity] = useState(0);
  const handleQuantityChange = (newQuantity: number) => {
    onQuantityUpdate(
      newQuantity,
      itemOption._key,
      itemId,
      value,
      prevQuantity,
      itemOption.maxAmount,
      comboSlotId
    );
  };

  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  // * Normally this logic is handled in the <Calories /> wrapper
  // * but seeing as MenuLineItem relies on boolean logic to show/hide the details
  // * its imported here so that an explicit falsy value can be provided
  // * (instead of providing a React Element)
  const hideUiCalories = useFlag(LaunchDarklyFlag.HIDE_UI_CALORIES);
  const formatJoules = useFormatJoules();

  const formattedJoules = formatJoules(Number(subtitle?.split(' ')[0]));
  const { formatMessage } = useIntl();

  const euFormattedNutritionText = formatMessage(
    { id: 'euNutritionValues' },
    { calories: subtitle, joules: formattedJoules }
  );

  const globalizedCalorieFormat = subtitle ? `${euFormattedNutritionText}` : '';

  let finalCalories;
  if (!hideUiCalories) {
    finalCalories = enableEUNutritionDisplay ? globalizedCalorieFormat : subtitle;
  }

  // Capture the state change from the auto adjustment from the modifier reducer
  const qty = useMemo(() => {
    setPrevQuantity(quantity);
    return quantity;
  }, [quantity]);

  return (
    <MenuLineItem
      bottomContent={
        <>
          {checked && itemOption.allowMultipleSelections && (
            <QuantityDetail>
              <Incrementor
                aria-label={`Quantity ${quantity} of ${name}`}
                value={qty}
                name={name}
                onChange={handleQuantityChange}
                min={1}
                max={itemOption.maxAmount}
              >
                <ModifierSelectionPrice>{formatMessage({ id: 'included' })}</ModifierSelectionPrice>
              </Incrementor>
            </QuantityDetail>
          )}
          <ModifierPrice price={price} />
        </>
      }
      data-testid={dataTestId}
      disabled={disabled}
      image={image}
      imageDescription={imageDescription}
      indicator={
        <SelectionIndicator
          data-testid={testId}
          type="radio"
          value={value}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          inputName={inputName}
          size={Size.REGULAR}
        />
      }
      name={name}
      selected={checked}
      details={finalCalories}
    />
  );
};

export default MenuOptionSelector;
//TODO: refactor picker aspects and combo-slots to implement this style
